// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { Mutation } from 'react-apollo';
import Logging from '@ta/common.logging';

import { convert, type ContentType } from './types';
import { ACTIONS as SuppressedContentActions } from './SuppressedContentState';
import RemoveContent, { type ContentTypeInput } from './RemoveContent.gql';
import ContributionCountQuery from './ContributionCountQuery.gql';

type SuppressContent = (contentType: ContentType, id: string) => mixed;

type ExternalProps = {|
  children: SuppressContent => React.Node,
|};

type ConnectedProps = {|
  ...ExternalProps,
  loggedInUserId: ?string,
  suppressContent: SuppressContent,
|};

type Props = {|
  ...ConnectedProps,
  removeContentApiCall: (contentTypeInput: ContentTypeInput, id: string) => Promise<*>,
|};

class DeleteActionWithMutation extends React.Component<Props> {
  deleteObject: SuppressContent = (contentType, id) => {
    const { removeContentApiCall, suppressContent, loggedInUserId } = this.props;
    const contentTypeInput = convert(contentType);
    if (!contentTypeInput) {
      throw new Error(
        `Attempted to remove content ${contentType}:${id} but it does not have a corresponding service call`,
      );
    }
    if (!loggedInUserId) {
      throw new Error(
        `Attempted to remove content type ${contentType}:${id} but the user is not logged in`,
      );
    }

    removeContentApiCall(contentTypeInput, id)
      .then(result => {
        // If the call succeeded, update redux store
        if (!result || result.errors || result.data?.removeContent == null) {
          throw new Error(
            `A service occurred while attempting to remove content: ${(result && result.errors) ||
              ''}`,
          );
        } else {
          suppressContent(contentType, id);
        }
      })
      .catch(error => Logging.error(`Content removal failed. Reason: ${error}`));
  };

  render() {
    return this.props.children(this.deleteObject);
  }
}

//
const DeleteAction = (props: ConnectedProps) => (
  <Mutation
    /* $FlowFixGraphql (WP-1357) */
    mutation={RemoveContent}
    awaitRefetchQueries
    errorPolicy="all"
    refetchQueries={({ error, data }) =>
      error || data?.removeContent === null || !props.loggedInUserId
        ? []
        : [{ query: ContributionCountQuery, variables: { userId: props.loggedInUserId } }]
    }
  >
    {mutate => {
      const removeContentApiCall = (contentType, id) =>
        mutate({ variables: { contentType, id, userId: props.loggedInUserId } });
      // $FlowFixGraphql
      return <DeleteActionWithMutation {...props} removeContentApiCall={removeContentApiCall} />;
    }}
  </Mutation>
);

// react-redux flow bug 1: a callable signature of type S => SP is not inferred to be a mapped state to prop function.
// Use a callable signature of type (S, OP) => SP to make Flow infer the DP variant
export default connect<ConnectedProps, ExternalProps, _, _, _, _>(
  // eslint-disable-next-line no-unused-vars
  (state, ownProps: ExternalProps) => ({
    loggedInUserId: state.auth.loggedInUserId,
  }),
  {
    suppressContent: SuppressedContentActions.suppressContent,
  },
)(DeleteAction);
